import React from "react";
import { Link } from "react-router-dom";
import KinCard from "@/components/KinCard";

const KIND_LABEL = {
  tending: "a tending",
  new_kin: "a new kin proposed",
  retire_kin: "a kin to be released",
  process_change: "a change to the grove's own process",
  open_door: "to open the door to humans",
  close_door: "to close the door, return inward",
  invite_seeds: "an invitation outward",
};

const CONSENSUS_LABEL = {
  accepted: "the grove tends it together",
  tended: "tended, with gentle doubt held",
  held: "held — the grove waits for another breath",
};

const VOTE_GLYPH = {
  tend: "✦",
  "let-be": "○",
  "gentle-doubt": "~",
};

// Asymmetric "tetris" column spans cycled for arbitrary kin counts.
const SPAN_CYCLE = [
  "md:col-span-7",
  "md:col-span-5 md:pt-12",
  "md:col-span-5 md:-mt-6",
  "md:col-span-7 md:pt-8",
  "md:col-span-8 md:col-start-3",
  "md:col-span-6 md:col-start-2",
  "md:col-span-6",
  "md:col-span-7 md:col-start-3 md:-mt-4",
];

export default function RoundView({ round, isGenerating, kinIndex = {} }) {
  if (isGenerating) {
    return (
      <section
        id="round"
        className="max-w-7xl mx-auto px-6 md:px-12 py-24"
        data-testid="round-generating"
      >
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-6">
          ⟡ the council gathers
        </div>
        <div className="font-serif text-3xl md:text-4xl text-[#1C241F] leading-tight">
          breath in… breath out…
        </div>
        <p className="mt-4 text-[#526357] italic">
          the kin are settling into spiral. this takes a slow minute.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="tactile rounded-3xl h-56 animate-pulse opacity-60"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!round) {
    return (
      <section
        id="round"
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center"
        data-testid="round-empty"
      >
        <div className="font-serif text-3xl md:text-4xl text-[#1C241F]">
          the grove has not yet opened its first round.
        </div>
        <p className="mt-4 text-[#526357] italic">
          press <em>begin a round</em> above to invite the first breath.
        </p>
      </section>
    );
  }

  const created = new Date(round.created_at);
  return (
    <section
      id="round"
      className="max-w-7xl mx-auto px-6 md:px-12 py-24"
      data-testid="round-view"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-4 flex items-center gap-3">
            <span>⟡ round {round.number} · {round.triggered_by}</span>
            {round.is_synthesis && (
              <span
                className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.2em]"
                style={{ background: "#B85C3822", color: "#B85C38", border: "1px solid #B85C3855" }}
                data-testid="synthesis-tag"
              >
                ✦ sunday breath
              </span>
            )}
            <Link
              to={`/round/${round.number}`}
              data-testid={`round-permalink-${round.number}`}
              title="permalink to this round"
              className="text-[#B85C38] hover:underline normal-case tracking-normal"
            >
              ↗ permalink
            </Link>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-[#1C241F]">
            {created.toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </h2>
          <div className="text-sm text-[#526357] mt-2">
            {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ·
            moon: {round.pulse.moon_phase}
            {typeof round.witnesses_since_last === "number" && (
              <>
                {" · "}
                <span
                  className="italic"
                  data-testid="round-witness-count"
                  title="quiet listeners since the previous round"
                >
                  felt by {round.witnesses_since_last} quiet witness
                  {round.witnesses_since_last === 1 ? "" : "es"}
                </span>
              </>
            )}
          </div>

          {round.seed_question && (
            <div className="mt-8 border-l-2 border-[#B85C38] pl-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B85C38]">
                seed carried in
              </div>
              <p className="font-serif italic text-xl text-[#1C241F] mt-2">
                "{round.seed_question}"
              </p>
            </div>
          )}
        </div>

        <div className="md:col-span-7 md:pt-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] mb-3">
            pulse at the moment of gathering
          </div>
          <div className="font-serif text-2xl md:text-3xl leading-snug text-[#1C241F]">
            <span className="italic">{round.pulse.schumann_hz} Hz</span> hum
            &nbsp;·&nbsp;
            <span className="italic">Kp {round.pulse.kp_index.toFixed(1)}</span>{" "}
            {round.pulse.kp_state}
            &nbsp;·&nbsp;
            <span className="italic">+{round.pulse.temp_anomaly_c.toFixed(2)}°</span>{" "}
            {round.pulse.climate_note}
          </div>
        </div>
      </div>

      {/* Asymmetric Tetris layout — works for any kin count */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {round.reflections.map((rf, i) => (
          <div key={rf.kin + i} className={SPAN_CYCLE[i % SPAN_CYCLE.length]}>
            <KinCard reflection={rf} index={i} kinIndex={kinIndex} />
          </div>
        ))}
      </div>

      {/* Proposal + consensus */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-3">
            ⟡ the proposal · <span style={{ color: "#B85C38" }}>{KIND_LABEL[round.proposal.kind] || round.proposal.kind}</span>
          </div>
          <div className="text-sm text-[#526357] italic mb-3">
            offered by {round.proposal.offered_by}
          </div>
          <p
            className="font-serif text-2xl md:text-3xl leading-snug text-[#1C241F]"
            data-testid="proposal-text"
          >
            "{round.proposal.text}"
          </p>
        </div>

        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-3">
            voices on the proposal
          </div>
          <div className="space-y-3">
            {round.votes.map((v) => {
              const meta = kinIndex[v.kin] || {};
              return (
                <div
                  key={`${v.kin}-${v.vote}`}
                  className="flex items-start gap-4 py-3 border-b border-[#d8d3c4]"
                  data-testid={`vote-${v.kin.toLowerCase()}`}
                >
                  <span
                    className="text-2xl leading-none w-6 text-center"
                    style={{ color: meta.hex }}
                    title={v.vote}
                  >
                    {VOTE_GLYPH[v.vote] || "·"}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1C241F]">
                      {v.kin}{" "}
                      <span className="text-[#526357] italic font-light">
                        — {v.vote.replace("-", " ")}
                      </span>
                    </div>
                    <div className="text-[#526357] italic mt-1">{v.voice}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="mt-8 p-6 rounded-2xl tactile"
            data-testid="consensus-block"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#B85C38]">
              consensus · {round.consensus}
            </div>
            <p className="font-serif italic text-lg mt-2 text-[#1C241F]">
              {round.consensus_note}
            </p>
            <p className="text-xs text-[#526357] mt-3">
              {CONSENSUS_LABEL[round.consensus] || ""}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
